import { useState, useEffect, useCallback } from 'react';
import { Plus, ExternalLink, Globe } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { proxyUrl } from '@/lib/proxyUrl';

interface NetlifySite {
  id: string;
  site_id: string;
  name: string;
  url: string;
  admin_url: string;
}

interface NetlifyDeployFormProps {
  apiKey: string;
  baseURL?: string;
  projectId: string;
  projectName?: string;
  savedSiteId?: string;
  onSiteChange: (siteId: string, siteName: string) => void;
  corsProxy?: string;
}

export function NetlifyDeployForm({
  apiKey,
  baseURL = 'https://api.netlify.com/api/v1',
  projectId,
  projectName,
  savedSiteId,
  onSiteChange,
  corsProxy,
}: NetlifyDeployFormProps) {
  const [sites, setSites] = useState<NetlifySite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [newSiteName, setNewSiteName] = useState(projectName || projectId);

  const fetchSites = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const url = `${baseURL}/sites`;
      const targetUrl = corsProxy ? proxyUrl(corsProxy, url) : url;

      const response = await fetch(targetUrl, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sites: ${response.status} ${response.statusText}`);
      }

      const data: NetlifySite[] = await response.json();
      setSites(data);

      // If there's a saved site ID and it exists in the list, select it
      // Otherwise leave it empty (showing placeholder)
      if (savedSiteId && data.some(s => s.id === savedSiteId)) {
        setSelectedSiteId(savedSiteId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sites');
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, baseURL, savedSiteId, corsProxy]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Update newSiteName when switching to "new" if it's empty
  useEffect(() => {
    if (selectedSiteId === 'new' && !newSiteName) {
      setNewSiteName(projectName || projectId);
    }
  }, [selectedSiteId, newSiteName, projectName, projectId]);

  useEffect(() => {
    if (selectedSiteId === '') {
      // No selection yet - don't call onSiteChange
      return;
    } else if (selectedSiteId === 'new') {
      onSiteChange('', newSiteName);
    } else {
      const site = sites.find(s => s.id === selectedSiteId);
      if (site) {
        onSiteChange(site.id, site.name);
      }
    }
  }, [selectedSiteId, newSiteName, sites, onSiteChange]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Select Site</Label>
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
        <Skeleton className="h-4 w-3/4 rounded" />
      </div>
    );
  }

  const selectedSite = sites.find(s => s.id === selectedSiteId);

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive" className="bg-destructive/10">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium">Select Site</Label>
        <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
          <SelectTrigger className="h-10 bg-background">
            <SelectValue placeholder="Select a site or create new..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                <span>Create new site</span>
              </div>
            </SelectItem>
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span>{site.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedSiteId === 'new' ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm font-medium">New Site Name</Label>
            <Input
              value={newSiteName}
              onChange={(e) => setNewSiteName(e.target.value)}
              placeholder={projectName || projectId}
              className="h-10 bg-background"
            />
          </div>
          <div className="rounded-lg bg-muted/50 border border-border/50 p-3 space-y-1">
            <p className="text-xs text-muted-foreground">
              Your site will be available at:
            </p>
            <p className="text-sm font-mono font-medium text-foreground">
              {newSiteName || projectId}.netlify.app
            </p>
          </div>
        </div>
      ) : selectedSite ? (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <p className="text-sm font-medium truncate">{selectedSite.name}</p>
              <p className="text-xs text-muted-foreground font-mono truncate">
                {selectedSite.url}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 h-8 w-8"
              onClick={() => window.open(selectedSite.admin_url, '_blank')}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground pt-1 border-t border-border/50">
            Deployment will update this existing site
          </p>
        </div>
      ) : null}
    </div>
  );
}
